import { atom } from "jotai";

import type { IStudioAlert } from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Alert.interface";
import type { StreamEvent } from "../services/eventBus/types";

/**
 * One firing of an alert. The lifecycle (`stage`) drives the AlertLayer's
 * render: it mounts during `enter`, transitions to `hold`, then `exit`,
 * then is removed entirely. The `event` payload carries the trigger data
 * so text bindings (`{last.follow.username}`, etc) interpolate against
 * the actual event that fired.
 */
export interface ActiveAlertInstance {
  instanceId: string;
  alert: IStudioAlert;
  event: StreamEvent;
  /** Absolute ms timestamp when this instance entered "enter" stage. */
  startedAt: number;
  stage: "enter" | "hold" | "exit";
}

export interface PendingAlertInstance {
  instanceId: string;
  alert: IStudioAlert;
  event: StreamEvent;
  /** ms timestamp when this was added to the queue. Used for FIFO. */
  enqueuedAt: number;
  /**
   * True when this instance originated from the AlertBuilder's "Test fire"
   * affordance (via `AlertEngine.testFire`) instead of a real bus event.
   * Used by telemetry to distinguish runtime fires (`alert_fired_runtime`
   * with `from_test: false`) from preview fires.
   */
  fromTest?: boolean;
}

export const activeAlertsAtom = atom<ActiveAlertInstance[]>([]);
export const pendingAlertsAtom = atom<PendingAlertInstance[]>([]);

/** Toggle on the AlertLayer to render alerts. False during builder preview, true on live output. */
export const alertRuntimeEnabledAtom = atom<boolean>(true);
